import { Maximize2 } from "lucide-react";

type Props = {
  src: string;
  srcSet?: string;
  alt: string;
  width: number;
  height: number;
  /** Shown in the fake address bar — framing is CSS, not baked into the
   * image, so the same capture works full-bleed in the lightbox. */
  urlLabel?: string;
  onExpand?: () => void;
  expandLabel?: string;
};

/** Reusable browser-framed product capture: gradient-glow backdrop, card ring,
 * CSS browser-chrome bar (dots + address pill), and the screenshot in an
 * aspect-locked box (zero CLS). Theme-aware (light/dark). */
export function FramedScreenshot({
  src,
  srcSet,
  alt,
  width,
  height,
  urlLabel = "adambusinesshub.com",
  onExpand,
  expandLabel,
}: Props) {
  return (
    <div className="group relative">
      {/* Accent glow behind the frame — same treatment family as the bento cards. */}
      <div
        className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-accent-500/15 via-transparent to-indigo-500/15 blur-2xl"
        aria-hidden
      />
      <div className="relative overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 dark:bg-slate-900 dark:shadow-black/40 dark:ring-white/10">
        {/* CSS browser chrome */}
        <div className="flex items-center gap-2 border-b border-slate-200/80 bg-slate-50 px-4 py-2.5 dark:border-white/5 dark:bg-slate-900/80">
          <span className="flex gap-1.5" aria-hidden>
            <span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-white/20" />
            <span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-white/20" />
            <span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-white/20" />
          </span>
          <span
            className="mx-auto flex-none rounded-full bg-white px-3 py-0.5 text-[10px] font-medium text-slate-400 ring-1 ring-slate-200 dark:bg-white/5 dark:text-slate-500 dark:ring-white/10"
            aria-hidden
          >
            {urlLabel}
          </span>
          <span className="w-[42px]" aria-hidden />
        </div>
        <div className="relative aspect-[16/10]">
          <img
            src={src}
            srcSet={srcSet}
            alt={alt}
            width={width}
            height={height}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              aria-label={expandLabel}
              title={expandLabel}
              className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-md ring-1 ring-slate-200 backdrop-blur transition-opacity hover:bg-white sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100 dark:bg-slate-800/90 dark:text-slate-200 dark:ring-white/10 dark:hover:bg-slate-800"
            >
              <Maximize2 size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
